import React, { useMemo, useState } from 'react';
import { Check, Info, Sparkles, Calculator as CalcIcon, Coins, RefreshCw, Rocket, FileBarChart2, Send, Wallet } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Container, SectionHeading, Btn, Pill } from '../components/ui';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import FAQAccordion from '../components/FAQAccordion';
import CTASection from '../components/CTASection';
import { PRICING_TIERS, PRICING_FAQ, PRICING_MODEL } from '../data/content';
import { cn } from '@/lib/utils';

const fmtUSD = (n) => `$${Math.round(n).toLocaleString('en-US')}`;
const fmtNum = (n) => n.toLocaleString('en-US');

const M = PRICING_MODEL;

// Returns the cheaper of Free vs Pro for a given monthly bill volume.
function estimateForBills(bills) {
  const b = Math.max(0, Math.floor(bills || 0));
  const freeCost = Math.max(0, b - M.freeBills) * M.freeRate;
  const proCost = M.proPrice + Math.max(0, b - M.proBills) * M.proRate;
  if (b <= M.freeBills) {
    return { plan: 'Free', base: 0, includedBills: M.freeBills, overageBills: 0, overageRate: M.freeRate, cost: 0 };
  }
  if (freeCost <= proCost) {
    return { plan: 'Free', base: 0, includedBills: M.freeBills, overageBills: b - M.freeBills, overageRate: M.freeRate, cost: freeCost };
  }
  return { plan: 'Pro', base: M.proPrice, includedBills: M.proBills, overageBills: Math.max(0, b - M.proBills), overageRate: M.proRate, cost: proCost };
}

const HOW = [
  { icon: Coins, title: 'One simple price per bill', text: 'Every bill includes collection, parsing (bill data extraction) and full UBM access. Free gives you 5 bills a month; Pro gives you 30 — then a low, predictable per-bill rate beyond.' },
  { icon: RefreshCw, title: 'Top-up & auto-recharge', text: 'Set a minimum balance and auto-recharge so processing never pauses. Bill credits and AI credits are purchased and managed inside the app.' },
  { icon: Rocket, title: 'Scale when you’re ready', text: 'Start free, upgrade to Pro in a click, and move to Enterprise for custom volume pricing and a fully managed service.' },
];

// AI agent credits — a SEPARATE prepaid wallet, distinct from bill-processing credits.
const AI_CREDITS_CAPS = [
  { icon: Rocket, title: 'Onboard & migrate in minutes', text: 'Agents onboard accounts and migrate data from a previous provider — no manual reformatting, loading and fixing.' },
  { icon: FileBarChart2, title: 'Generate reports on demand', text: 'Ask for a specific report and the agent assembles it from your utility data.' },
  { icon: Send, title: 'Reach suppliers in bulk', text: 'Agents contact suppliers and chase actions across many accounts at once.' },
  { icon: Wallet, title: 'Prepaid, pay-as-you-use', text: 'Top up credits, spend only on the work you run, and stay in full control of your budget — no surprises.' },
];

function TierCard({ tier }) {
  return (
    <div
      data-testid={`pricing-tier-card-${tier.name.toLowerCase()}`}
      className={cn(
        'relative flex h-full flex-col rounded-2xl border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1',
        tier.highlight
          ? 'border-brand bg-white shadow-lg ring-1 ring-brand/30 dark:bg-white/[0.04]'
          : 'border-slate-200 bg-white hover:border-brand/40 hover:shadow-lg dark:border-white/10 dark:bg-white/[0.03]'
      )}
    >
      {tier.highlight && (
        <span className="absolute -top-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1 rounded-full bg-brand px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
          <Sparkles className="h-3 w-3" /> Most popular
        </span>
      )}
      <h3 className="font-display text-lg font-bold text-slate-900 dark:text-white">{tier.name}</h3>
      <div className="mt-3 flex items-baseline gap-1.5">
        <span className="font-display text-3xl font-bold text-slate-900 dark:text-white">{tier.price}</span>
        <span className="text-sm text-slate-500 dark:text-slate-400">{tier.priceSub}</span>
      </div>
      <p className="mt-1 text-sm font-medium text-brand">{tier.range}</p>
      <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{tier.blurb}</p>

      <ul className="mt-5 flex-1 space-y-2.5">
        {tier.features.map((f) => {
          const text = typeof f === 'string' ? f : f.text;
          const tip = typeof f === 'string' ? null : f.tip;
          return (
            <li key={text} className="flex items-start gap-2.5 text-sm leading-relaxed text-slate-700 dark:text-slate-200">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand">
                <Check className="h-3 w-3" />
              </span>
              <span className="flex items-start gap-1">
                {text}
                {tip && (
                  <span className="group/tip relative inline-flex" data-testid="pricing-feature-tip">
                    <Info className="mt-[3px] h-3.5 w-3.5 shrink-0 cursor-help text-slate-400 transition-colors hover:text-brand" />
                    <span className="pointer-events-none absolute bottom-full left-1/2 z-30 mb-1.5 w-56 -translate-x-1/2 rounded-lg bg-slate-900 px-3 py-2 text-xs font-normal leading-snug text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover/tip:opacity-100 dark:bg-slate-700">
                      {tip}
                    </span>
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      {tier.note && (
        <p className="mt-4 rounded-lg bg-slate-50 px-3 py-2 text-xs leading-relaxed text-slate-500 dark:bg-white/[0.04] dark:text-slate-400">
          {tier.note}
        </p>
      )}

      {(() => {
        const linkProps = tier.href ? { href: tier.href } : { to: tier.ctaTo };
        return (
          <Btn
            {...linkProps}
            full
            size="md"
            variant={tier.highlight ? 'primary' : 'outline'}
            className="mt-6"
            data-testid={`pricing-tier-cta-${tier.name.toLowerCase()}`}
          >
            {tier.cta}
          </Btn>
        );
      })()}
    </div>
  );
}

function Calculator() {
  const [bills, setBills] = useState(40);
  const [postal, setPostal] = useState(0);

  const est = useMemo(() => {
    const b = Math.max(0, Math.floor(Number(bills) || 0));
    const p = Math.max(0, Math.floor(Number(postal) || 0));
    const postalCost = p * M.postalRate;
    const sel = estimateForBills(b);
    return { bills: b, postalCost, ...sel, total: sel.cost + postalCost };
  }, [bills, postal]);

  const isFree = est.plan === 'Free';

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
      {/* Inputs */}
      <div className="lg:col-span-3 rounded-2xl border border-slate-200 bg-white p-6 dark:border-white/10 dark:bg-white/[0.03] sm:p-8">
        <div className="flex items-center gap-2 text-brand">
          <CalcIcon className="h-5 w-5" />
          <span className="text-xs font-semibold uppercase tracking-[0.18em]">Estimate your plan</span>
        </div>

        <p className="mt-6 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
          Enter how many bills you expect to process each month. We compare Free ($0 · 5 bills included, then
          ${M.freeRate.toFixed(2)}/bill) against Pro (${M.proPrice}/mo · {M.proBills} bills included, then
          ${M.proRate.toFixed(2)}/bill) and show you the cheaper option.
        </p>

        <div className="mt-6">
          <label htmlFor="calc-bills" className="flex items-center gap-1.5 text-sm font-semibold text-slate-900 dark:text-white">
            <FileBarChart2 className="h-4 w-4 text-brand" /> Bills per month
          </label>
          <input
            id="calc-bills"
            type="number"
            min="0"
            value={bills}
            onChange={(e) => setBills(e.target.value)}
            placeholder="e.g. 40"
            className="mt-2 h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-lg font-semibold text-slate-900 outline-none transition-colors focus:border-brand dark:border-white/15 dark:bg-white/[0.04] dark:text-white"
            data-testid="pricing-calc-bills-input"
            aria-label="Bills per month"
          />
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            Each price includes collection, parsing (bill data extraction) and full UBM access.
          </p>
        </div>

        <div className="mt-7 border-t border-slate-100 pt-6 dark:border-white/10">
          <label htmlFor="calc-postal" className="text-sm font-semibold text-slate-900 dark:text-white">
            Bills arriving by post / month (optional)
          </label>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Postal mail capture add-on at ${M.postalRate.toFixed(2)} per bill (paid plans).
          </p>
          <input
            id="calc-postal"
            type="number"
            min="0"
            value={postal}
            onChange={(e) => setPostal(e.target.value)}
            className="mt-3 h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition-colors focus:border-brand dark:border-white/15 dark:bg-white/[0.04] dark:text-white"
            data-testid="pricing-calc-postal-input"
            aria-label="Postal bills per month"
          />
        </div>
      </div>

      {/* Result */}
      <div className="lg:col-span-2 flex flex-col rounded-2xl border border-brand/30 bg-brand-ink p-6 text-white sm:p-8">
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-300">Estimated monthly cost</span>

        {est.total === 0 ? (
          <>
            <div className="mt-3 font-display text-4xl font-bold" data-testid="pricing-calc-total">Free</div>
            <div className="mt-2 inline-flex w-fit items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-0.5 text-xs font-semibold text-brand-300" data-testid="pricing-calc-plan">Free plan</div>
            <p className="mt-3 text-sm leading-relaxed text-slate-300">
              Up to {M.freeBills} bills a month are free forever. Process more and we&apos;ll pick the cheapest of Free or Pro for you.
            </p>
            <Btn href="https://app.billportal.ai/signup?src=web&plan=free" variant="white" full arrow className="mt-6">Start free</Btn>
          </>
        ) : (
          <>
            <div className="mt-3 font-display text-4xl font-bold" data-testid="pricing-calc-total">
              {fmtUSD(est.total)}<span className="text-base font-medium text-slate-400"> / mo</span>
            </div>
            <div className="mt-2 inline-flex w-fit items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-0.5 text-xs font-semibold text-brand-300" data-testid="pricing-calc-plan">
              {est.plan} plan · cheapest for {fmtNum(est.bills)} bills
            </div>
            <div className="mt-4 space-y-3 text-sm">
              {est.base > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-slate-300">{est.plan} subscription</span>
                  <span className="font-semibold">{fmtUSD(est.base)}</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-slate-300">{fmtNum(est.includedBills)} bills included</span>
                <span className="font-semibold">{fmtUSD(0)}</span>
              </div>
              {est.overageBills > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-slate-300">{fmtNum(est.overageBills)} extra × ${est.overageRate.toFixed(2)}</span>
                  <span className="font-semibold">{fmtUSD(est.overageBills * est.overageRate)}</span>
                </div>
              )}
              {est.postalCost > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-slate-300">Postal capture add-on</span>
                  <span className="font-semibold">{fmtUSD(est.postalCost)}</span>
                </div>
              )}
              <div className="flex items-center justify-between border-t border-white/15 pt-3">
                <span className="font-semibold">Estimated total</span>
                <span className="font-display text-xl font-bold text-brand-300">{fmtUSD(est.total)}</span>
              </div>
            </div>
            <Btn href={`https://app.billportal.ai/signup?src=web&plan=${isFree ? 'free' : 'pro'}`} variant="white" full arrow className="mt-6">Get started</Btn>
          </>
        )}

        <p className="mt-auto pt-6 text-[11px] leading-relaxed text-slate-400">
          <Info className="mr-1 inline h-3 w-3 align-[-1px]" />
          Estimates are for planning purposes only and are not a quote. Need a fully managed, customised or white-labelled setup? Enterprise has you covered — <a href="/contact" className="underline hover:text-white">contact us</a>. Credits and auto-recharge are managed inside the BillPortal app.
        </p>
      </div>
    </div>
  );
}

export default function Pricing() {
  const { t } = useLanguage();
  return (
    <div>
      <PageHero
        eyebrow={t('nav.pricing')}
        title="Simple, transparent, pay-only-for-what-you-use pricing"
        subtitle="Two simple plans — start free with 5 bills a month (no card), or go Pro at $30/month for 30 bills included. Every price includes collection, parsing and full UBM access."
      >
        <Btn href="https://app.billportal.ai/signup?src=web&plan=free" size="lg" arrow>Start free</Btn>
        <Btn href="#calculator" size="lg" variant="outline">Estimate your cost</Btn>
      </PageHero>

      {/* Quick facts */}
      <section className="px-5 pt-10 sm:px-8">
        <Container>
          <div className="flex flex-wrap items-center justify-center gap-2.5">
            <Pill><Check className="h-3.5 w-3.5 text-brand" /> Free · 5 bills / month</Pill>
            <Pill><Check className="h-3.5 w-3.5 text-brand" /> Pro $30/mo · 30 bills</Pill>
            <Pill><Check className="h-3.5 w-3.5 text-brand" /> $1.39/bill (Free) → $1.00/bill (Pro)</Pill>
            <Pill><Check className="h-3.5 w-3.5 text-brand" /> Postal capture +$1.50/bill</Pill>
            <Pill><Check className="h-3.5 w-3.5 text-brand" /> Includes collection, parsing & full UBM access</Pill>
          </div>
        </Container>
      </section>

      {/* Tiers */}
      <section className="px-5 py-12 sm:px-8 sm:py-16">
        <Container>
          <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {PRICING_TIERS.map((tier, i) => (
              <Reveal key={tier.name} delay={i * 60}><TierCard tier={tier} /></Reveal>
            ))}
          </div>
          <p className="mx-auto mt-6 max-w-3xl text-center text-xs leading-relaxed text-slate-500 dark:text-slate-400" data-testid="pricing-note">
            Please note: every price includes collection, parsing (bill data extraction) and full UBM access.
          </p>
        </Container>
      </section>

      {/* Calculator */}
      <section id="calculator" className="scroll-mt-20 bg-slate-50 px-5 py-16 dark:bg-white/[0.02] sm:px-8 sm:py-24">
        <Container>
          <Reveal>
            <SectionHeading
              eyebrow="Pricing calculator"
              title="Estimate your monthly cost"
              subtitle="Enter your monthly accounts by commodity — electric, gas, water and other — and we apply the right per-line rate automatically."
            />
          </Reveal>
          <Reveal delay={80} className="mt-12"><Calculator /></Reveal>
        </Container>
      </section>

      {/* How credits work */}
      <section className="px-5 py-16 sm:px-8 sm:py-24">
        <Container>
          <Reveal>
            <SectionHeading eyebrow="How it works" title="Credits that map to how you bill" subtitle="Transparent, prepaid and tied directly to the accounts you process — nothing hidden." />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-3">
            {HOW.map((h, i) => (
              <Reveal key={h.title} delay={i * 60}>
                <div className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 dark:border-white/10 dark:bg-white/[0.03]">
                  <span className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10 text-brand">
                    <h.icon className="h-5 w-5" />
                  </span>
                  <h3 className="text-base font-semibold text-slate-900 dark:text-white">{h.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{h.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* AI agents on prepaid credits (separate from bill credits) */}
      <section className="bg-slate-50 px-5 py-16 dark:bg-white/[0.02] sm:px-8 sm:py-24" data-testid="ai-agent-credits-section">
        <Container>
          <Reveal>
            <SectionHeading
              eyebrow="AI agents"
              title="Put AI agents to work — on prepaid credits"
              subtitle="Separate from bill credits, AI agent credits cover the heavier lifting: onboarding accounts, migrating data from a previous provider, generating tailored reports and contacting suppliers in bulk. You pay only for the work you run, with full control over your budget."
            />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-5">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:col-span-3">
              {AI_CREDITS_CAPS.map((c, i) => (
                <Reveal key={c.title} delay={i * 60}>
                  <div
                    data-testid={`ai-credit-cap-${i}`}
                    className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 dark:border-white/10 dark:bg-white/[0.03]"
                  >
                    <span className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10 text-brand">
                      <c.icon className="h-5 w-5" />
                    </span>
                    <h3 className="text-base font-semibold text-slate-900 dark:text-white">{c.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{c.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={120} className="lg:col-span-2">
              <div
                data-testid="ai-credit-highlight"
                className="flex h-full flex-col rounded-2xl border border-brand/30 bg-brand-ink p-6 text-white sm:p-8"
              >
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-300">A real example</span>
                <div className="mt-3 font-display text-3xl font-bold leading-tight" data-testid="ai-credit-stat">
                  100+ accounts onboarded in under an hour
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-300">
                  We&apos;ve seen an agent complete an onboarding of over 100 accounts in about an hour — work that would
                  traditionally take a team many hours or several days to reformat, load and fix. A migration like that can
                  start from around <span className="font-semibold text-white">$20 in credits</span>, depending on complexity —
                  a fraction of the usual onboarding cost.
                </p>
                <p className="mt-5 text-[11px] leading-relaxed text-slate-400">
                  <Info className="mr-1 inline h-3 w-3 align-[-1px]" />
                  Illustrative estimate for one use case; actual credits vary with complexity. We suggest running a
                  feasibility check on your free account first.
                </p>
                <Btn href="https://app.billportal.ai/signup?src=web" variant="white" full arrow className="mt-6">Start free &amp; check feasibility</Btn>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 px-5 py-16 dark:bg-white/[0.02] sm:px-8 sm:py-20">
        <Container>
          <Reveal><SectionHeading eyebrow="FAQ" title="Pricing questions, answered" /></Reveal>
          <Reveal delay={80} className="mt-10"><FAQAccordion items={PRICING_FAQ} /></Reveal>
        </Container>
      </section>

      <CTASection
        title="Ready to take control of your utility spend?"
        subtitle="Start free, or book a walkthrough and we'll map BillPortal — and your credits — to your portfolio."
      />
    </div>
  );
}
